/**
 * ALOHA QUEEN SUPERNOVA
 * Copyright © 2025 Malcolm KH Lee III. All Rights Reserved.
 * 
 * Enhanced 8K Galaxy Background with 40s Supernova + ʻOumuamua
 */

import React, { useEffect, useRef } from 'react';

const Enhanced8KGalaxyBackground = ({ quality = 'high' }) => {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    let animationFrameId;
    let time = 0;

    // Quality settings
    const qualitySettings = {
      low: { stars: 1000, nebula: 50, planetDetail: 1 },
      medium: { stars: 2000, nebula: 100, planetDetail: 2 },
      high: { stars: 3000, nebula: 150, planetDetail: 3 },
      ultra: { stars: 5000, nebula: 200, planetDetail: 4 }
    };
    
    const settings = qualitySettings[quality] || qualitySettings.high;

    // Set canvas to full window size
    const resizeCanvas = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };
    resizeCanvas();
    window.addEventListener('resize', resizeCanvas);

    // Stars configuration based on quality setting
    const stars = Array.from({ length: settings.stars }, () => ({
      x: Math.random() * canvas.width,
      y: Math.random() * canvas.height,
      size: Math.random() * 2.5,
      speed: Math.random() * 0.5 + 0.1,
      opacity: Math.random() * 0.8 + 0.2,
      twinkleSpeed: Math.random() * 0.02 + 0.01
    }));

    // Nebula particles for depth (quality based)
    const nebulaParticles = Array.from({ length: settings.nebula }, () => ({
      x: Math.random() * canvas.width,
      y: Math.random() * canvas.height,
      size: Math.random() * 100 + 50,
      hue: Math.random() * 60 + 260, // Purple-blue range
      speed: Math.random() * 0.2 + 0.05,
      opacity: Math.random() * 0.15 + 0.05
    }));

    // Detailed planets
    const planets = [
      { 
        name: 'Mercury', distance: 120, size: 8, color: '#8C7853', speed: 0.04, 
        angle: 0, details: { craters: true }
      },
      { 
        name: 'Venus', distance: 180, size: 14, color: '#FFC649', speed: 0.03, 
        angle: Math.PI / 3, details: { atmosphere: true }
      },
      { 
        name: 'Earth', distance: 250, size: 15, color: '#4A90E2', speed: 0.02, 
        angle: Math.PI / 2, details: { clouds: true, continents: true }
      },
      { 
        name: 'Mars', distance: 320, size: 12, color: '#CD5C5C', speed: 0.018, 
        angle: Math.PI, details: { poles: true }
      },
      { 
        name: 'Jupiter', distance: 450, size: 35, color: '#C88B3A', speed: 0.008, 
        angle: Math.PI * 1.2, details: { bands: true, redSpot: true }
      },
      { 
        name: 'Saturn', distance: 580, size: 30, color: '#F4E3B2', speed: 0.006, 
        angle: Math.PI * 1.5, details: { rings: true }
      },
      { 
        name: 'Uranus', distance: 700, size: 22, color: '#4FD0E7', speed: 0.004, 
        angle: Math.PI * 1.8, details: { tilt: true }
      },
      { 
        name: 'Neptune', distance: 800, size: 21, color: '#4169E1', speed: 0.003, 
        angle: Math.PI * 2, details: { storms: true }
      }
    ];

    // Comets
    const comets = Array.from({ length: 3 }, (_, i) => ({
      x: Math.random() * canvas.width,
      y: Math.random() * canvas.height,
      speed: Math.random() * 3 + 2,
      angle: Math.random() * Math.PI * 2,
      tail: []
    }));

    // ʻOumuamua - The Interstellar Traveler (Special Feature)
    const oumuamua = {
      x: -100,
      y: canvas.height * 0.3,
      speed: 4,
      angle: Math.PI / 6, // Diagonal trajectory
      length: 80,
      width: 15,
      rotation: 0,
      rotationSpeed: 0.05,
      color: '#8B4513',
      trail: []
    };

    // Satellites
    const satellites = Array.from({ length: 12 }, () => ({
      x: Math.random() * canvas.width,
      y: Math.random() * canvas.height,
      size: 2,
      blinkPhase: Math.random() * Math.PI * 2,
      orbitRadius: Math.random() * 100 + 50,
      orbitSpeed: Math.random() * 0.02 + 0.01,
      angle: Math.random() * Math.PI * 2
    }));

    // Supernova state
    let supernovaActive = false;
    let supernovaProgress = 0;
    const SUPERNOVA_START_TIME = 40000; // 40 seconds
    const SUPERNOVA_DURATION = 3000; // 3 seconds duration

    const drawStars = () => {
      stars.forEach(star => {
        const twinkle = Math.sin(time * star.twinkleSpeed) * 0.3 + 0.7;
        ctx.fillStyle = `rgba(255, 255, 255, ${star.opacity * twinkle})`;
        ctx.beginPath();
        ctx.arc(star.x, star.y, star.size, 0, Math.PI * 2);
        ctx.fill();

        // Add glow for larger stars
        if (star.size > 1.5) {
          const gradient = ctx.createRadialGradient(star.x, star.y, 0, star.x, star.y, star.size * 3);
          gradient.addColorStop(0, `rgba(255, 255, 255, ${star.opacity * 0.3})`);
          gradient.addColorStop(1, 'rgba(255, 255, 255, 0)');
          ctx.fillStyle = gradient;
          ctx.fill();
        }

        // Move stars
        star.y += star.speed;
        if (star.y > canvas.height) {
          star.y = 0;
          star.x = Math.random() * canvas.width;
        }
      });
    };

    const drawNebula = () => {
      nebulaParticles.forEach(particle => {
        const gradient = ctx.createRadialGradient(
          particle.x, particle.y, 0,
          particle.x, particle.y, particle.size
        );
        gradient.addColorStop(0, `hsla(${particle.hue}, 80%, 60%, ${particle.opacity})`);
        gradient.addColorStop(0.5, `hsla(${particle.hue + 20}, 70%, 50%, ${particle.opacity * 0.5})`);
        gradient.addColorStop(1, 'rgba(0, 0, 0, 0)');
        
        ctx.fillStyle = gradient;
        ctx.fillRect(
          particle.x - particle.size,
          particle.y - particle.size,
          particle.size * 2,
          particle.size * 2
        );

        particle.y += particle.speed;
        if (particle.y > canvas.height + particle.size) {
          particle.y = -particle.size;
          particle.x = Math.random() * canvas.width;
        }
      });
    };

    const drawPlanetDetails = (planet, x, y) => {
      ctx.save();
      ctx.translate(x, y);

      // Enhanced 8K/4K quality planets with detailed textures
      const gradient = ctx.createRadialGradient(0, 0, 0, 0, 0, planet.size);
      
      // Add multiple color stops for depth and texture
      gradient.addColorStop(0, lightenColor(planet.color, 30));
      gradient.addColorStop(0.3, planet.color);
      gradient.addColorStop(0.7, darkenColor(planet.color, 20));
      gradient.addColorStop(0.9, darkenColor(planet.color, 40));
      gradient.addColorStop(1, '#000');
      
      ctx.fillStyle = gradient;
      ctx.beginPath();
      ctx.arc(0, 0, planet.size, 0, Math.PI * 2);
      ctx.fill();

      // Add atmospheric glow for gas giants
      if (planet.size > 20) {
        const glowGradient = ctx.createRadialGradient(0, 0, planet.size, 0, 0, planet.size * 1.3);
        glowGradient.addColorStop(0, `${planet.color}00`);
        glowGradient.addColorStop(0.5, `${planet.color}33`);
        glowGradient.addColorStop(1, `${planet.color}00`);
        ctx.fillStyle = glowGradient;
        ctx.beginPath();
        ctx.arc(0, 0, planet.size * 1.3, 0, Math.PI * 2);
        ctx.fill();
      }

      // Enhanced specific details based on planet
      if (planet.details.rings) {
        // Saturn's enhanced ring system (multiple rings)
        const ringColors = ['rgba(244, 227, 178, 0.7)', 'rgba(230, 210, 160, 0.5)', 'rgba(220, 200, 150, 0.3)'];
        ringColors.forEach((color, i) => {
          ctx.strokeStyle = color;
          ctx.lineWidth = 4 - i;
          ctx.beginPath();
          ctx.ellipse(0, 0, planet.size * (2 + i * 0.3), planet.size * (0.4 + i * 0.1), 0, 0, Math.PI * 2);
          ctx.stroke();
        });
        
        // Ring shadow on planet
        ctx.fillStyle = 'rgba(0, 0, 0, 0.3)';
        ctx.fillRect(-planet.size, -planet.size * 0.1, planet.size * 2, planet.size * 0.2);
      }

      if (planet.details.bands) {
        // Jupiter's atmospheric bands (enhanced)
        for (let i = 0; i < 6; i++) {
          const y = -planet.size + (i * planet.size / 3);
          ctx.fillStyle = i % 2 === 0 ? 'rgba(200, 140, 70, 0.3)' : 'rgba(180, 120, 50, 0.3)';
          ctx.fillRect(-planet.size, y, planet.size * 2, planet.size / 3);
        }
      }

      if (planet.details.redSpot) {
        // Jupiter's Great Red Spot (enhanced)
        const spotGradient = ctx.createRadialGradient(
          planet.size * 0.3, 0, 0,
          planet.size * 0.3, 0, planet.size * 0.35
        );
        spotGradient.addColorStop(0, 'rgba(220, 100, 80, 0.9)');
        spotGradient.addColorStop(0.7, 'rgba(200, 80, 60, 0.7)');
        spotGradient.addColorStop(1, 'rgba(180, 60, 40, 0)');
        
        ctx.fillStyle = spotGradient;
        ctx.beginPath();
        ctx.ellipse(planet.size * 0.3, 0, planet.size * 0.35, planet.size * 0.25, 0, 0, Math.PI * 2);
        ctx.fill();
      }

      if (planet.details.clouds) {
        // Earth's clouds and continents (enhanced)
        ctx.strokeStyle = 'rgba(255, 255, 255, 0.4)';
        ctx.lineWidth = 2.5;
        ctx.beginPath();
        ctx.arc(0, 0, planet.size + 2, 0, Math.PI * 2);
        ctx.stroke();
        
        // Add cloud patterns
        ctx.fillStyle = 'rgba(255, 255, 255, 0.2)';
        for (let i = 0; i < 5; i++) {
          const cloudX = (Math.random() - 0.5) * planet.size * 1.5;
          const cloudY = (Math.random() - 0.5) * planet.size * 1.5;
          ctx.beginPath();
          ctx.arc(cloudX, cloudY, planet.size * 0.2, 0, Math.PI * 2);
          ctx.fill();
        }
      }

      if (planet.details.poles) {
        // Mars polar ice caps (enhanced)
        ctx.fillStyle = 'rgba(255, 255, 255, 0.8)';
        ctx.beginPath();
        ctx.arc(0, -planet.size * 0.8, planet.size * 0.3, 0, Math.PI * 2);
        ctx.fill();
        ctx.beginPath();
        ctx.arc(0, planet.size * 0.8, planet.size * 0.25, 0, Math.PI * 2);
        ctx.fill();
      }

      // Add surface texture for all planets
      const textureSize = planet.size * 0.1;
      ctx.fillStyle = 'rgba(0, 0, 0, 0.1)';
      for (let i = 0; i < 20; i++) {
        const tx = (Math.random() - 0.5) * planet.size * 1.5;
        const ty = (Math.random() - 0.5) * planet.size * 1.5;
        const distance = Math.sqrt(tx * tx + ty * ty);
        if (distance < planet.size) {
          ctx.beginPath();
          ctx.arc(tx, ty, textureSize, 0, Math.PI * 2);
          ctx.fill();
        }
      }

      ctx.restore();
    };

    // Helper functions for color manipulation
    const lightenColor = (hex, percent) => {
      const num = parseInt(hex.replace('#', ''), 16);
      const r = Math.min(255, ((num >> 16) + percent));
      const g = Math.min(255, (((num >> 8) & 0x00FF) + percent));
      const b = Math.min(255, ((num & 0x0000FF) + percent));
      return `rgb(${r}, ${g}, ${b})`;
    };

    const darkenColor = (hex, percent) => {
      const num = parseInt(hex.replace('#', ''), 16);
      const r = Math.max(0, ((num >> 16) - percent));
      const g = Math.max(0, (((num >> 8) & 0x00FF) - percent));
      const b = Math.max(0, ((num & 0x0000FF) - percent));
      return `rgb(${r}, ${g}, ${b})`;
    };

    const drawPlanets = () => {
      const centerX = canvas.width / 2;
      const centerY = canvas.height / 2;

      // Draw sun at center
      const sunGradient = ctx.createRadialGradient(centerX, centerY, 0, centerX, centerY, 50);
      sunGradient.addColorStop(0, '#FFF');
      sunGradient.addColorStop(0.3, '#FFD700');
      sunGradient.addColorStop(0.7, '#FFA500');
      sunGradient.addColorStop(1, 'rgba(255, 69, 0, 0)');
      ctx.fillStyle = sunGradient;
      ctx.beginPath();
      ctx.arc(centerX, centerY, 50, 0, Math.PI * 2);
      ctx.fill();

      planets.forEach(planet => {
        planet.angle += planet.speed * 0.01;
        const x = centerX + Math.cos(planet.angle) * planet.distance;
        const y = centerY + Math.sin(planet.angle) * planet.distance * 0.5;

        // Draw orbit path
        ctx.strokeStyle = 'rgba(255, 255, 255, 0.1)';
        ctx.lineWidth = 1;
        ctx.beginPath();
        ctx.ellipse(centerX, centerY, planet.distance, planet.distance * 0.5, 0, 0, Math.PI * 2);
        ctx.stroke();

        // Draw planet with details
        drawPlanetDetails(planet, x, y);

        // Draw label
        ctx.fillStyle = 'rgba(255, 255, 255, 0.8)';
        ctx.font = '12px Arial';
        ctx.fillText(planet.name, x - 20, y - planet.size - 10);
      });
    };

    const drawComets = () => {
      comets.forEach(comet => {
        // Move comet
        comet.x += Math.cos(comet.angle) * comet.speed;
        comet.y += Math.sin(comet.angle) * comet.speed;

        // Reset if off screen
        if (comet.x < 0 || comet.x > canvas.width || comet.y < 0 || comet.y > canvas.height) {
          comet.x = Math.random() * canvas.width;
          comet.y = Math.random() * canvas.height;
          comet.angle = Math.random() * Math.PI * 2;
        }

        // Add to tail
        comet.tail.unshift({ x: comet.x, y: comet.y });
        if (comet.tail.length > 30) comet.tail.pop();

        // Draw tail
        comet.tail.forEach((pos, i) => {
          const alpha = 1 - (i / comet.tail.length);
          ctx.fillStyle = `rgba(100, 200, 255, ${alpha * 0.5})`;
          ctx.beginPath();
          ctx.arc(pos.x, pos.y, 3 * (1 - i / comet.tail.length), 0, Math.PI * 2);
          ctx.fill();
        });

        // Draw comet head
        ctx.fillStyle = '#FFF';
        ctx.beginPath();
        ctx.arc(comet.x, comet.y, 4, 0, Math.PI * 2);
        ctx.fill();
      });
    };

    const drawSatellites = () => {
      satellites.forEach(sat => {
        sat.angle += sat.orbitSpeed;
        sat.blinkPhase += 0.1;
        
        const blink = Math.sin(sat.blinkPhase) * 0.5 + 0.5;
        ctx.fillStyle = `rgba(255, 255, 0, ${blink})`;
        ctx.beginPath();
        ctx.arc(sat.x, sat.y, sat.size, 0, Math.PI * 2);
        ctx.fill();

        // Add blinking light effect
        if (blink > 0.7) {
          ctx.strokeStyle = `rgba(255, 255, 0, ${(blink - 0.7) * 2})`;
          ctx.lineWidth = 2;
          ctx.beginPath();
          ctx.arc(sat.x, sat.y, sat.size + 3, 0, Math.PI * 2);
          ctx.stroke();
        }
      });
    };

    const drawOumuamua = () => {
      // Move ʻOumuamua - the interstellar traveler
      oumuamua.x += Math.cos(oumuamua.angle) * oumuamua.speed;
      oumuamua.y += Math.sin(oumuamua.angle) * oumuamua.speed;
      oumuamua.rotation += oumuamua.rotationSpeed;

      // Reset if off screen (travels across and disappears)
      if (oumuamua.x > canvas.width + 200) {
        oumuamua.x = -200;
        oumuamua.y = Math.random() * canvas.height;
      }

      // Add to trail
      oumuamua.trail.unshift({ x: oumuamua.x, y: oumuamua.y });
      if (oumuamua.trail.length > 50) oumuamua.trail.pop();

      ctx.save();
      ctx.translate(oumuamua.x, oumuamua.y);
      ctx.rotate(oumuamua.rotation);

      // Draw cigar-shaped object (elongated)
      ctx.fillStyle = oumuamua.color;
      ctx.fillRect(-oumuamua.length / 2, -oumuamua.width / 2, oumuamua.length, oumuamua.width);

      // Add glow effect
      ctx.shadowBlur = 20;
      ctx.shadowColor = '#FFD700';
      ctx.fillStyle = 'rgba(139, 69, 19, 0.8)';
      ctx.fillRect(-oumuamua.length / 2, -oumuamua.width / 2, oumuamua.length, oumuamua.width);

      ctx.restore();

      // Draw trail
      oumuamua.trail.forEach((pos, i) => {
        const alpha = 1 - (i / oumuamua.trail.length);
        ctx.fillStyle = `rgba(139, 69, 19, ${alpha * 0.3})`;
        ctx.beginPath();
        ctx.arc(pos.x, pos.y, 5 * (1 - i / oumuamua.trail.length), 0, Math.PI * 2);
        ctx.fill();
      });

      // Label (when visible)
      if (oumuamua.x > 0 && oumuamua.x < canvas.width) {
        ctx.fillStyle = 'rgba(255, 215, 0, 0.9)';
        ctx.font = 'bold 14px Arial';
        ctx.fillText("ʻOumuamua - The Traveler", oumuamua.x + 50, oumuamua.y - 30);
        ctx.font = '10px Arial';
        ctx.fillStyle = 'rgba(255, 255, 255, 0.7)';
        ctx.fillText("Interstellar Scout", oumuamua.x + 50, oumuamua.y - 15);
      }
    };

    const drawSupernova = (progress) => {
      const centerX = canvas.width / 2;
      const centerY = canvas.height / 2;
      
      // Explosion expansion
      const maxRadius = Math.max(canvas.width, canvas.height) * 1.5;
      const radius = maxRadius * progress;
      
      // Multiple shockwave rings
      for (let i = 0; i < 5; i++) {
        const ringProgress = Math.max(0, progress - i * 0.1);
        const ringRadius = maxRadius * ringProgress;
        const ringOpacity = Math.max(0, 0.5 - ringProgress) * (1 - progress);
        
        const gradient = ctx.createRadialGradient(
          centerX, centerY, ringRadius * 0.8,
          centerX, centerY, ringRadius
        );
        gradient.addColorStop(0, `rgba(255, 255, 255, ${ringOpacity})`);
        gradient.addColorStop(0.5, `rgba(255, 200, 100, ${ringOpacity * 0.7})`);
        gradient.addColorStop(1, `rgba(255, 100, 50, 0)`);
        
        ctx.fillStyle = gradient;
        ctx.beginPath();
        ctx.arc(centerX, centerY, ringRadius, 0, Math.PI * 2);
        ctx.fill();
      }

      // Core explosion
      if (progress < 0.5) {
        const coreSize = 200 * (1 - progress * 2);
        const coreGradient = ctx.createRadialGradient(
          centerX, centerY, 0,
          centerX, centerY, coreSize
        );
        coreGradient.addColorStop(0, '#FFF');
        coreGradient.addColorStop(0.3, '#FFD700');
        coreGradient.addColorStop(0.7, '#FF4500');
        coreGradient.addColorStop(1, 'rgba(255, 69, 0, 0)');
        
        ctx.fillStyle = coreGradient;
        ctx.beginPath();
        ctx.arc(centerX, centerY, coreSize, 0, Math.PI * 2);
        ctx.fill();
      }

      // Particles shooting out
      const particleCount = 100;
      for (let i = 0; i < particleCount; i++) {
        const angle = (Math.PI * 2 * i) / particleCount;
        const distance = radius * (0.5 + Math.random() * 0.5);
        const x = centerX + Math.cos(angle) * distance;
        const y = centerY + Math.sin(angle) * distance;
        const size = 3 + Math.random() * 3;
        const opacity = Math.max(0, 0.8 - progress);
        
        ctx.fillStyle = `rgba(255, ${200 - progress * 100}, ${100 - progress * 50}, ${opacity})`;
        ctx.beginPath();
        ctx.arc(x, y, size, 0, Math.PI * 2);
        ctx.fill();
      }
    };

    const animate = () => {
      time += 0.01;
      const elapsedTime = Date.now() % (SUPERNOVA_START_TIME + SUPERNOVA_DURATION + 5000);
      
      // Clear canvas
      ctx.fillStyle = '#000';
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      // Draw elements
      drawNebula();
      drawStars();
      
      // Check if supernova should trigger
      if (elapsedTime >= SUPERNOVA_START_TIME && elapsedTime < SUPERNOVA_START_TIME + SUPERNOVA_DURATION) {
        supernovaActive = true;
        supernovaProgress = (elapsedTime - SUPERNOVA_START_TIME) / SUPERNOVA_DURATION;
        drawSupernova(supernovaProgress);
      } else {
        supernovaActive = false;
        supernovaProgress = 0;
        drawPlanets();
        drawComets();
        drawOumuamua(); // Draw the interstellar traveler
        drawSatellites();
      }

      animationFrameId = requestAnimationFrame(animate);
    };

    animate();

    return () => {
      window.removeEventListener('resize', resizeCanvas);
      cancelAnimationFrame(animationFrameId);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      className="fixed top-0 left-0 w-full h-full -z-10"
      style={{ background: '#000' }}
    />
  );
};

export default Enhanced8KGalaxyBackground;
